'use client';

import { Button } from '@tranquility-ai/theme-components/button';
import { Popover as TpPopover } from '@tranquility-ai/theme-components/popover';
import '@tranquility-ai/theme-components/popover.css';
import { GetBuildId } from '@/shared/functions';
import { useHoverPopover } from '@/hooks/useHoverPopover';
import { useTimePilotT } from '@/i18n/useTimePilotT';

const LOGO_BUILD_INFO_POPOVER_ID = 'logo-build-info-popover';

type PrimaryNavBrandProps = {
  tenantPrefix?: string;
  userId: string;
  helioTraceEnabled: boolean;
};

/** Build/version readout on the logo — Popover so the Helio CTA is clickable. */
export function PrimaryNavBrand({
  tenantPrefix,
  userId,
  helioTraceEnabled,
}: PrimaryNavBrandProps) {
  const t = useTimePilotT();
  const { open, setOpen, triggerRef, triggerProps } = useHoverPopover({
    surfaceId: LOGO_BUILD_INFO_POPOVER_ID,
  });

  const buildLabel = GetBuildId() ? `v${GetBuildId()}` : '';

  return (
    <TpPopover
      id={LOGO_BUILD_INFO_POPOVER_ID}
      isOpen={open}
      onOpenChange={setOpen}
      placement="bottom-start"
      variant="inverted"
      sideOffset={4}
      collisionPadding={8}
      maxWidth="18rem"
      showArrow
      content={
        <div className="flex flex-col gap-1 text-sm leading-snug">
          <div className="break-words">
            {t("nav.productVersion", { version: buildLabel }).trim()}
          </div>
          <div className="break-words">{t("nav.tenant", { name: tenantPrefix || "N/A" })}</div>
          <div className="break-words">{t("nav.user", { id: userId })}</div>
        </div>
      }
      footer={
        helioTraceEnabled ? (
          <Button
            type="button"
            style="solid"
            color="accent"
            background="brand"
            size="sm"
            className="w-full min-w-0"
            onClick={() => {
              window.location.href = '/immigration-reports';
            }}
          >
            <span className="inline-flex w-full min-w-0 items-center justify-center gap-2">
              <span className="whitespace-nowrap">{t("nav.goTo")}</span>
              <img
                src="/helio-logo-white.svg"
                alt=""
                className="h-5 w-auto max-w-[5rem] shrink-0 object-contain"
                width={80}
                height={20}
              />
            </span>
          </Button>
        ) : undefined
      }
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={t("nav.buildInfo")}
        aria-haspopup="dialog"
        className="m-0 flex cursor-pointer flex-row items-center border-none bg-transparent p-0"
        {...triggerProps}
      >
        <div className="flex h-[29px] w-[112px] flex-row items-center overflow-hidden sm:h-[48.276px] sm:w-[140px] sm:py-[3px]">
          <img
            className="relative h-full w-full object-contain object-left"
            alt=""
            src="/tp-white-r.png"
            width={140}
            height={48.276}
            role="presentation"
          />
        </div>
      </button>
    </TpPopover>
  );
}
